import React, { useState } from "react";
import {
  CalendarIcon,
  CheckCircleIcon,
  CircleTickedIcon,
  ClockIcon,
  EditIcon,
  EmailIcon,
  InvoiceDashboardIcon,
  UserIcon,
  ViewIcon,
} from "../Icons";
import { PhoneIcon } from "../Store/StoreIcons";
import { FaCreditCard, FaMapPin, FaShip } from "react-icons/fa";
import SettingsIcon from "mdi-react/SettingsIcon";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  customerFullName,
  fetchActionsUtil,
  sendFormData,
} from "../../utils/helpers";
import { useMutation, useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import { isEmpty } from "lodash";
import NewCustomCustomerModal from "../NewCustomCustomerModal";
import { useCurrencies } from "../../utils/hooks";
import currency from "currency.js";
import Avatar from "../utils/Avatar";
import CheckDecagramIcon from "mdi-react/CheckDecagramIcon";
import { GrMail } from "react-icons/gr";
import { CreateCustomersEmail } from "../CustomersEmail/SendCustomersEmail";
import EyeOffOutlineIcon from "mdi-react/EyeOffOutlineIcon";
import EyeOutlineIcon from "mdi-react/EyeOutlineIcon";

export default function CustomerDetailPage() {
  const { backendUrl } = useAuth();
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("customerId");
  const [customerData, setCustomerData] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { prevailingCurrencySymbol } = useCurrencies();
  const [
    showCustomCustomModal,
    setShowCustomCreateNewCustomerModal,
  ] = useState();
  const [inputData, setInputData] = useState("");
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery(
    ["GET_CUSTOMER", customerId],
    () => fetchActionsUtil(`${backendUrl}/api/customers/single/${customerId}`),
    {
      onSuccess: ({ customer }) => {
        let planData = JSON.parse(customer?.CustomerTypeData);
        planData =
          typeof planData === "string" ? JSON.parse(planData) : planData;

        setCustomerData({ ...customer, CustomerTypeData: planData });
      },
      enabled: true,
      keepPreviousData: true,
    }
  );

  const fetchScheduleTransactions = async (queryParams) => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/invoice/sheduled-tansactions?customerId=${customerId}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    data.total = data?.transactions?.reduce(
      (acc, curr) => acc + Number(curr.subTotal),
      0
    );
    data.transactions = data?.transactions?.flatMap((el) => {
      if (el?.invoice) {
        const items = JSON.parse(el.invoice)?.items;
        return Array.isArray(items) ? items : [items];
      }
      return [];
    });

    return data;
  };

  const {
    data: transaction = {
      count: 0,
      transactions: [],
    },
  } = useQuery(
    ["GET_SCHEDULE_TRANSACTIONS", customerId],
    () => fetchScheduleTransactions(),
    {
      keepPreviousData: true,
      enabled: !!customerId,
    }
  );

  const [activeTab, setActiveTab] = useState("personal");

  const parseCustomerTypeData = (data) => {
    if (typeof data === "string") {
      try {
        return JSON.parse(data.replace(/\\/g, ""));
      } catch (e) {
        return {};
      }
    }
    return data || {};
  };

  const membershipData = parseCustomerTypeData(customerData.CustomerTypeData);

  const formatCurrency = (amount) => {
    return currency(amount).format({
      separator: ",",
      symbol: prevailingCurrencySymbol,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  const getStatusBadge = (isActive, isVerified) => {
    if (isActive === "1") {
      return (
        <div
          className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill"
          style={{
            backgroundColor: "#dcecf7",
            color: "#0a4a95",
            border: "1px solid #7eb6f7",
          }}
        >
          <CheckCircleIcon size={14} />
          <small className="fw-semibold">Active</small>
        </div>
      );
    } else if (isActive === "0") {
      return (
        <div
          className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill"
          style={{
            backgroundColor: "#f2f7fc",
            color: "#677888",
            border: "1px solid #a7b9cb",
          }}
        >
          <ClockIcon size={14} />
          <small className="fw-semibold">Pending</small>
        </div>
      );
    }
    return (
      <div
        className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill"
        style={{
          backgroundColor: "#fee",
          color: "#fe5b52",
          border: "1px solid #fe5b52",
        }}
      >
        <CircleTickedIcon size={14} />
        <small className="fw-semibold">Inactive</small>
      </div>
    );
  };

  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <div
      role="button"
      className={`d-flex align-items-center gap-2 px-4 py-3 rounded-3 border-0 transition-all`}
      onClick={onClick}
      style={{
        backgroundColor: isActive ? "#2463ae" : "#ffffff",
        color: isActive ? "#ffffff" : "#334155",
        boxShadow: isActive
          ? "0 4px 15px rgba(36, 99, 174, 0.3)"
          : "0 2px 8px rgba(0,0,0,0.08)",
        cursor: "pointer",
        transform: isActive ? "translateY(-1px)" : "none",
        borderLeft: isActive ? "none" : "4px solid transparent",
        borderLeftColor:
          !isActive && id === activeTab ? "#2463ae" : "transparent",
      }}
    >
      <Icon size={18} />
      <span className="fw-semibold">{label}</span>
    </div>
  );

  const InfoCard = ({ children, title, className = "" }) => (
    <div
      className={`border-0 rounded-4 ${className}`}
      style={{
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 32px rgba(36, 99, 174, 0.08)",
        border: "1px solid #e2e8f0",
      }}
    >
      <div className="p-4">
        {title && (
          <h5 className="fw-bold mb-4" style={{ color: "#0f172a" }}>
            {title}
          </h5>
        )}
        {children}
      </div>
    </div>
  );

  const StatCard = ({ icon: Icon, title, value, color = "#2463ae" }) => (
    <div
      className="p-4 rounded-3 h-100"
      style={{
        backgroundColor: color === "#2463ae" ? "#ecf4fe" : "#f2f7fc",
        border: `1px solid ${color === "#2463ae" ? "#7eb6f7" : "#a7b9cb"}`,
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <div
          className="p-3 rounded-circle"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
        <div>
          <h6 className="mb-1 fw-bold" style={{ color: "#0f172a" }}>
            {value}
          </h6>
          <small className="fw-medium" style={{ color: "#334155" }}>
            {title}
          </small>
        </div>
      </div>
    </div>
  );

  const handleShowCustomCustomerModal = (el) => {
    const customerData = JSON.stringify(el?.CustomerTypeData);
    setSelectedCustomer({ ...el, CustomerTypeData: customerData });
    setShowCustomCreateNewCustomerModal(true);
  };

  const uploadProfileImage = useMutation((payload) =>
    sendFormData(`${backendUrl}/api/customers/upload-profile-pics`, payload)
  );

  const OnChangeInput = (e) => {
    const { files } = e.target;
    setInputData(files[0]);
    const formData = new FormData();
    formData.append("profilePics", files[0]);
    formData?.append("Cust_ID", customerData?.Cust_ID);
    uploadProfileImage.mutate(formData);
  };
  return (
    !isLoading &&
    !isEmpty(customerData) && (
      <div
        className="min-vh-100"
        style={{
          backgroundColor: "#f8faff",
          padding: "2rem 1rem",
        }}
      >
        <div className="container-fluid">
          {/* Header Section */}
          {/* <div className="row mb-4">
            <div className="col-12">
              <div
                className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 p-4 rounded-4"
                style={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 8px 32px rgba(36, 99, 174, 0.1)",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                    style={{
                      backgroundColor: "#ecf4fe",
                      border: "1px solid #7eb6f7",
                      color: "#2463ae",
                    }}
                  >
                    <ChevronLeftIcon size={18} />
                    <span className="fw-semibold">Back</span>
                  </button>
                  <div>
                    <h2 className="mb-1 fw-bold" style={{ color: "#0f172a" }}>
                      {customerData.FirstName} {customerData.LastName}
                    </h2>
                    <p className="mb-0" style={{ color: "#334155" }}>
                      Customer ID:{" "}
                      <span className="font-monospace fw-semibold">
                        {customerData.Cust_ID}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {getStatusBadge(
                    customerData.activateCustomer,
                    customerData.isVerified
                  )}
                  <button
                    className="btn px-4 py-2 rounded-pill fw-semibold d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#2463ae",
                      border: "none",
                      color: "white",
                      boxShadow: "0 4px 15px rgba(36, 99, 174, 0.3)",
                    }}
                  >
                    <EditIcon size={16} />
                    Edit Customer
                  </button>
                </div>
              </div>
            </div>
          </div> */}

          {/* Customer Overview Cards */}
          <div className="row mb-4 g-3">
            <div className="col-xl-8">
              <InfoCard>
                <div className="row align-items-center">
                  <div className="col-lg-3 text-center mb-3 mb-lg-0">
                    <div className="position-relative d-inline-block">
                      {/* <div
                        className="rounded-circle text-white d-flex align-items-center justify-content-center mx-auto"
                        style={{
                          width: "100px",
                          height: "100px",
                          backgroundColor: "#2463ae",
                          fontSize: "2.5rem",
                          fontWeight: "700",
                          boxShadow: "0 8px 32px rgba(36, 99, 174, 0.3)",
                        }}
                      >
                        {customerData.FirstName.charAt(0)}
                        {customerData.LastName.charAt(0)}
                      </div> */}

                      <div className="">
                        <Avatar
                          onFileUpload={OnChangeInput}
                          image={inputData}
                          name={customerFullName(customerData)}
                          style={{ width: "9rem", height: "9rem" }}
                          nameInput="profilePics"
                          userId={customerData.Cust_ID}
                        />
                      </div>
                    </div>
                    <h4
                      className="mt-3 mb-1 fw-bold"
                      style={{ color: "#0f172a" }}
                    >
                      {customerFullName(customerData)}
                      {customerData.isVerified ? (
                        <CheckDecagramIcon
                          size="1em"
                          className="text-success"
                        />
                      ) : null}
                    </h4>
                    <p className="fw-medium" style={{ color: "#334155" }}>
                      {customerData.Profession}
                    </p>
                  </div>
                  <div className="col-lg-9">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{ backgroundColor: "#f4f7fa" }}
                        >
                          <EmailIcon size={20} style={{ color: "#2463ae" }} />
                          <div>
                            <small
                              className="fw-medium"
                              style={{ color: "#94a3b8" }}
                            >
                              Email
                            </small>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.Email}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{ backgroundColor: "#f4f7fa" }}
                        >
                          <PhoneIcon size={20} style={{ color: "#2463ae" }} />
                          <div>
                            <small
                              className="fw-medium"
                              style={{ color: "#94a3b8" }}
                            >
                              Phone
                            </small>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.PhoneNo1}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{ backgroundColor: "#f4f7fa" }}
                        >
                          <FaMapPin size={20} style={{ color: "#2463ae" }} />
                          <div>
                            <small
                              className="fw-medium"
                              style={{ color: "#94a3b8" }}
                            >
                              Address
                            </small>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.CompanyAddress}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{ backgroundColor: "#f4f7fa" }}
                        >
                          <CalendarIcon
                            size={20}
                            style={{ color: "#2463ae" }}
                          />
                          <div>
                            <small
                              className="fw-medium"
                              style={{ color: "#94a3b8" }}
                            >
                              Member Since
                            </small>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {formatDate(customerData.dateOfSubscription)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className=" d-flex justify-content-end">
                        <div
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{
                            backgroundColor: "#f4f7fa",
                            width: "fit-content",
                          }}
                        >
                          <button
                            onClick={() => setShowEmailModal(true)}
                            className="btn btn-primary  py-2 "
                          >
                            <GrMail /> Send Email
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </InfoCard>
            </div>
            <div className="col-xl-4">
              <div className="row g-3 h-100">
                <div className="col-12">
                  <StatCard
                    icon={FaCreditCard}
                    title="Membership Type"
                    value={customerData.TransType}
                    color="#2463ae"
                  />
                </div>
                <div className="col-12">
                  <StatCard
                    icon={UserIcon}
                    title="Reference Person"
                    value={customerData.ReferenceName}
                    color="#126dd8"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="d-flex gap-3 flex-wrap p-3 rounded-4"
                style={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(36, 99, 174, 0.08)",
                  border: "1px solid #e2e8f0",
                }}
              >
                <TabButton
                  id="dashboard"
                  label="Customer's Dashboard"
                  icon={InvoiceDashboardIcon}
                  isActive={activeTab === "dashboard"}
                  onClick={() =>
                    navigate(
                      `/sales-and-invoicing/customer-overview?customerId=${
                        customerData.Cust_ID
                      }`
                    )
                  }
                />
                <TabButton
                  id="personal"
                  label="Personal Details"
                  icon={UserIcon}
                  isActive={activeTab === "personal"}
                  onClick={() => setActiveTab("personal")}
                />
                <TabButton
                  id="vessel"
                  label="Vessel Information"
                  icon={FaShip}
                  isActive={activeTab === "vessel"}
                  onClick={() => setActiveTab("vessel")}
                />
                <TabButton
                  id="membership"
                  label="Membership & Billing"
                  icon={FaCreditCard}
                  isActive={activeTab === "membership"}
                  onClick={() => setActiveTab("membership")}
                />
                <TabButton
                  id="settings"
                  label="Account Settings"
                  icon={SettingsIcon}
                  isActive={activeTab === "settings"}
                  onClick={() => setActiveTab("settings")}
                />
                <TabButton
                  id="edit"
                  label="Edit Account"
                  icon={EditIcon}
                  onClick={() => handleShowCustomCustomerModal(customerData)}
                />

                <TabButton
                  id="view-mails"
                  label="View Email"
                  icon={ViewIcon}
                  onClick={() =>
                    navigate(
                      `/send-customers-email?email=${customerData?.Email}`
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="row">
            <div className="col-12">
              {activeTab === "personal" && (
                <InfoCard title="Personal Information">
                  <div className="row g-4">
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              FULL NAME
                            </label>
                            <p
                              className="mb-0 fw-semibold h6"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.FirstName} {customerData.MiddleName}{" "}
                              {customerData.LastName}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              DATE OF BIRTH
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {formatDate(customerData.DOB)}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              PHONE NUMBERS
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.PhoneNo1}
                            </p>
                            {customerData.PhoneNo2 && (
                              <p
                                className="mb-0 fw-semibold"
                                style={{ color: "#334155" }}
                              >
                                {customerData.PhoneNo2}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              EMAIL ADDRESS
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.Email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              SPOUSE NAME
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.SpouseName || "Not Specified"}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              PROFESSION
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.Profession}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              ADDRESS
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.CompanyAddress}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              LOCATION
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.LGA}, {customerData.state},{" "}
                              {customerData.country}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}

              {activeTab === "vessel" && (
                <InfoCard title="Vessel Information">
                  <div className="row g-4">
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              VESSEL NAME
                            </label>
                            <p
                              className="mb-0 fw-semibold h6"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.VesselName || "Not Specified"}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              MANUFACTURER
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.Manufacturer || "Not Specified"}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              CALL SIGN
                            </label>
                            <p
                              className="mb-0 fw-semibold font-monospace"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.CallSign || "Not Specified"}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-3 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              VESSEL TYPE
                            </label>
                            <p
                              className="mb-0 fw-semibold"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.VesselType || "Not Specified"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div
                            className="p-3 rounded-3 text-center h-100"
                            style={{
                              backgroundColor: "#ecf4fe",
                            }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              YEAR BUILT
                            </label>
                            <p
                              className="mb-0 fw-bold h5"
                              style={{ color: "#2463ae" }}
                            >
                              {customerData.YearBuilt || "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div
                            className="p-3 rounded-3 text-center h-100"
                            style={{ backgroundColor: "#d2e7ff" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              LENGTH (LOA)
                            </label>
                            <p
                              className="mb-0 fw-bold h5"
                              style={{ color: "#126dd8" }}
                            >
                              {customerData.LOA
                                ? `${customerData.LOA} ft`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div
                            className="p-3 rounded-3 text-center h-100"
                            style={{ backgroundColor: "#f2f7fc" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              BEAM
                            </label>
                            <p
                              className="mb-0 fw-bold h5"
                              style={{ color: "#0a4a95" }}
                            >
                              {customerData.Beam
                                ? `${customerData.Beam} ft`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div
                            className="p-3 rounded-3 text-center h-100"
                            style={{ backgroundColor: "#d8e6f5" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              DRAFT
                            </label>
                            <p
                              className="mb-0 fw-bold h5"
                              style={{ color: "#788898" }}
                            >
                              {customerData.Draft
                                ? `${customerData.Draft} ft`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}

              {activeTab === "membership" && (
                <InfoCard title="Membership & Billing Information">
                  <div className="row g-4">
                    <div className="col-lg-8">
                      <div className="row g-4">
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{
                              backgroundColor: "#ecf4fe",
                            }}
                          >
                            <h6
                              className="fw-bold mb-3"
                              style={{ color: "#0f172a" }}
                            >
                              Membership Details
                            </h6>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div>
                                  <small
                                    className="fw-bold"
                                    style={{ color: "#94a3b8" }}
                                  >
                                    MEMBERSHIP TYPE
                                  </small>
                                  <p
                                    className="mb-0 fw-semibold h6"
                                    style={{ color: "#0f172a" }}
                                  >
                                    {customerData.TransType}
                                  </p>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div>
                                  <small
                                    className="fw-bold"
                                    style={{ color: "#94a3b8" }}
                                  >
                                    CATEGORY
                                  </small>
                                  <p
                                    className="mb-0 fw-semibold h6"
                                    style={{ color: "#0f172a" }}
                                  >
                                    {membershipData.categoryType}
                                  </p>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div>
                                  <small
                                    className="fw-bold"
                                    style={{ color: "#94a3b8" }}
                                  >
                                    SUBSCRIPTION DATE
                                  </small>
                                  <p
                                    className="mb-0 fw-semibold"
                                    style={{ color: "#0f172a" }}
                                  >
                                    {formatDate(
                                      customerData.dateOfSubscription
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div>
                                  <small
                                    className="fw-bold"
                                    style={{ color: "#94a3b8" }}
                                  >
                                    EXPIRATION DATE
                                  </small>
                                  <p
                                    className="mb-0 fw-semibold"
                                    style={{ color: "#0f172a" }}
                                  >
                                    {formatDate(customerData.activationDate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <h6
                              className="fw-bold mb-4"
                              style={{ color: "#0f172a" }}
                            >
                              Subscription Plan
                            </h6>

                            {Boolean(
                              Number(customerData?.initialActivateCustomer)
                            ) ? (
                              <>
                                <div className="row g-3">
                                  <div className="">
                                    <div
                                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                      style={{ borderColor: "#e2e8f0" }}
                                    >
                                      <span
                                        className="fw-medium"
                                        style={{ color: "#334155" }}
                                      >
                                        Entrance Fee
                                      </span>
                                      <span
                                        className="fw-bold"
                                        style={{ color: "#0f172a" }}
                                      >
                                        {formatCurrency(
                                          membershipData.entranceFee
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                      style={{ borderColor: "#e2e8f0" }}
                                    >
                                      <span
                                        className="fw-medium"
                                        style={{ color: "#334155" }}
                                      >
                                        Development Fee
                                      </span>
                                      <span
                                        className="fw-bold"
                                        style={{ color: "#0f172a" }}
                                      >
                                        {formatCurrency(
                                          membershipData.developmentFee
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="">
                                    <div
                                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                      style={{ borderColor: "#e2e8f0" }}
                                    >
                                      <span
                                        className="fw-medium"
                                        style={{ color: "#334155" }}
                                      >
                                        Annual Subscription
                                      </span>
                                      <span
                                        className="fw-bold"
                                        style={{ color: "#0f172a" }}
                                      >
                                        {formatCurrency(
                                          membershipData.annualSubscriptionFee
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                      style={{ borderColor: "#e2e8f0" }}
                                    >
                                      <span
                                        className="fw-medium"
                                        style={{ color: "#334155" }}
                                      >
                                        Bar Account Top-up
                                      </span>
                                      <span
                                        className="fw-bold"
                                        style={{ color: "#0f172a" }}
                                      >
                                        {formatCurrency(
                                          membershipData.topUpOnBarAccount
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className="mt-4 pt-3 border-top"
                                  style={{ borderColor: "#e2e8f0" }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span
                                      className="fw-bold h6"
                                      style={{ color: "#0f172a" }}
                                    >
                                      Total Amount
                                    </span>
                                    <span
                                      className="fw-bold h5"
                                      style={{ color: "#2463ae" }}
                                    >
                                      {formatCurrency(
                                        membershipData?.totalAmount
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <p
                                className="mb-0 fw-bold h5 text-center"
                                style={{ color: "#2463ae" }}
                              >
                                No Active subscription
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div
                        className="p-4 rounded-3 "
                        style={{
                          backgroundColor: "#d2e7ff",
                          overflowY: "auto",
                          maxHeight: "465px",
                        }}
                      >
                        <h6
                          className="fw-bold mb-4"
                          style={{ color: "#0f172a" }}
                        >
                          Upcoming Bill
                        </h6>
                        {Boolean(
                          Number(customerData?.initialActivateCustomer)
                        ) ? (
                          <>
                            {transaction?.transactions?.map((el, id) => (
                              <div
                                key={id}
                                className="d-flex justify-content-between align-items-center mb-3"
                              >
                                <span
                                  className="fw-medium"
                                  style={{ color: "#334155" }}
                                >
                                  {el?.Item_Name}
                                </span>
                                <span
                                  className="fw-bold h6"
                                  style={{ color: "#126dd8" }}
                                >
                                  {formatCurrency(el?.SubTotal)}
                                </span>
                              </div>
                            ))}

                            <div className="mb-3">
                              <span
                                className="fw-bold h6"
                                style={{ color: "#0f172a" }}
                              >
                                Total
                              </span>

                              <div className="mt-1">
                                <div className="d-flex justify-content-between">
                                  <span
                                    className="fw-bold h5"
                                    style={{ color: "#2463ae" }}
                                  >
                                    {formatCurrency(transaction?.total)}
                                  </span>
                                </div>
                                {/* <div className="d-flex justify-content-between">
                              <span
                                className="small"
                                style={{ color: "#334155" }}
                              >
                                End Date:
                              </span>
                              <span
                                className="small fw-semibold"
                                style={{ color: "#0f172a" }}
                              >
                                0
                                 {formatDate(
                                  customerData?.Customer_CreditLimit
                                    ?.creditLimitEndDate
                                )} 
                              </span>
                            </div> */}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="mb-3">
                            <span
                              className="fw-bold h6"
                              style={{ color: "#0f172a" }}
                            >
                              Total
                            </span>

                            <div className="mt-1">
                              <div className="d-flex justify-content-between">
                                <span
                                  className="fw-bold h5"
                                  style={{ color: "#2463ae" }}
                                >
                                  {formatCurrency(0)}
                                </span>
                              </div>
                              {/* <div className="d-flex justify-content-between">
                              <span
                                className="small"
                                style={{ color: "#334155" }}
                              >
                                End Date:
                              </span>
                              <span
                                className="small fw-semibold"
                                style={{ color: "#0f172a" }}
                              >
                                0
                                 {formatDate(
                                  customerData?.Customer_CreditLimit
                                    ?.creditLimitEndDate
                                )} 
                              </span>
                            </div> */}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}

              {activeTab === "settings" && (
                <InfoCard title="Account Settings">
                  <div className="row g-4">
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              ACCOUNT STATUS
                            </label>
                            <div className="mt-2">
                              {getStatusBadge(
                                customerData.activateCustomer,
                                customerData.isVerified
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              EMAIL VERIFICATION
                            </label>
                            <div className="mt-2">
                              {customerData.isVerified ? (
                                <div
                                  className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill"
                                  style={{
                                    backgroundColor: "#dcecf7",
                                    color: "#0a4a95",
                                  }}
                                >
                                  <CheckCircleIcon size={14} />
                                  <small className="fw-semibold">
                                    Verified
                                  </small>
                                </div>
                              ) : (
                                <div
                                  className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill"
                                  style={{
                                    backgroundColor: "#f2f7fc",
                                    color: "#677888",
                                  }}
                                >
                                  <ClockIcon size={14} />
                                  <small className="fw-semibold">
                                    Unverified
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              HOW THEY FOUND US
                            </label>
                            <p
                              className="mb-0 fw-semibold mt-2"
                              style={{ color: "#0f172a" }}
                            >
                              {customerData.How_Did_Know}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{
                              backgroundColor: "#ecf4fe",
                            }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              REGISTRATION DATE
                            </label>
                            <p
                              className="mb-0 fw-semibold h6 mt-2"
                              style={{ color: "#2463ae" }}
                            >
                              {formatDate(customerData.DateLog)}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              VERIFICATION TOKEN
                            </label>
                            <p
                              className="mb-0 font-monospace fw-semibold mt-2 small"
                              style={{
                                color: "#0f172a",
                                wordBreak: "break-all",
                                backgroundColor: "#e2e8f0",
                                padding: "0.5rem",
                                borderRadius: "0.25rem",
                              }}
                            >
                              {customerData.verifyToken || "Not Generated"}
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            className="p-4 rounded-3"
                            style={{ backgroundColor: "#f4f7fa" }}
                          >
                            <label
                              className="fw-bold small"
                              style={{ color: "#94a3b8" }}
                            >
                              RENEWAL SUBSCRIPTION ID
                            </label>
                            <p
                              className="mb-0 font-monospace fw-semibold mt-2 small"
                              style={{
                                color: "#0f172a",
                                wordBreak: "break-all",
                                backgroundColor: "#e2e8f0",
                                padding: "0.5rem",
                                borderRadius: "0.25rem",
                              }}
                            >
                              {customerData.renewalSubscriptionId ||
                                "Not Generated"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}
            </div>
          </div>
        </div>
        {/* <ModalLoader show={uploadProfileImage.isLoading || isLoading} /> */}

        {showCustomCustomModal && (
          <NewCustomCustomerModal
            showCreateNewCustomerModal={showCustomCustomModal}
            setShowCreateNewCustomerModal={setShowCustomCreateNewCustomerModal}
            refetch={refetch}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
          />
        )}
        {showEmailModal && (
          <CreateCustomersEmail
            show={showEmailModal}
            onclick={setShowEmailModal}
            refetch={() => {}}
          />
        )}
      </div>
    )
  );
}
